/**
 * 试打结果预览模态框（v2.1）
 *
 * 功能：
 * - 展示试打结果（前20行）
 * - 高亮显示结果列
 * - 支持图片和视频预览
 * - 支持导出CSV
 * - 提供"继续调整"和"满意，开始全量打标"选项
 */
import React, { useMemo } from 'react';
import { Modal, Table, Typography, Tag, Space, Button, Tooltip, message } from 'antd';
import { FileTextOutlined, DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import ImageCell from './ExcelViewer/ImageCell';
import VideoCell from './ExcelViewer/VideoCell';

const { Text } = Typography;

/**
 * 解析 URL 映射格式
 * 输入: "image_1:https://...,image_2:https://..."
 * 输出: ["https://...", "https://..."]
 */
const parseUrlMapping = (text) => {
  if (!text || text === 'None' || text === 'null') {
    return [];
  }

  const textStr = String(text).trim();

  // 如果是单个 URL（不包含逗号分隔的映射），直接返回
  if (textStr.startsWith('http://') || textStr.startsWith('https://')) {
    if (!textStr.includes(',')) {
      return [textStr];
    }
    // 检查是否是简单的逗号分隔 URL 列表（没有 key: 前缀）
    if (!textStr.match(/[a-zA-Z_]+\d*:https?:\/\//)) {
      return textStr.split(',').map(u => u.trim()).filter(u => u);
    }
  }

  // 解析映射格式: key1:url1,key2:url2
  const urls = [];
  // 使用正则分割，确保在 key:http 格式前分割
  const parts = textStr.split(/,(?=[a-zA-Z_]+\d*:https?:\/\/)/);

  for (const part of parts) {
    const match = part.match(/^[a-zA-Z_]+\d*:(https?:\/\/.+)$/);
    if (match) {
      urls.push(match[1].trim());
    } else if (part.startsWith('http://') || part.startsWith('https://')) {
      urls.push(part.trim());
    }
  }

  return urls;
};

/**
 * 检测URL的类型（图片/视频/普通文本）
 */
const detectUrlType = (url) => {
  if (!url || typeof url !== 'string') return 'text';

  const urlLower = url.toLowerCase();

  // 检测图片URL
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  if (imageExtensions.some(ext => urlLower.includes(ext))) {
    return 'image';
  }

  // 检测视频URL
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
  if (videoExtensions.some(ext => urlLower.includes(ext))) {
    return 'video';
  }

  // 检测常见图片/视频托管服务的URL模式
  if (urlLower.includes('image') || urlLower.includes('img') || urlLower.includes('photo')) {
    return 'image';
  }

  if (urlLower.includes('video') || urlLower.includes('vid')) {
    return 'video';
  }

  return 'text';
};

const TrialResultModal = ({ visible, onClose, data, filename, onConfirm }) => {
  // 构建表格列配置
  const columns = useMemo(() => {
    if (!data || !data.columns) return [];

    return data.columns.map(col => {
      const isResultColumn = col === data.result_column;

      // 渲染单元格内容
      const renderCell = (text) => {
        // 先尝试解析 URL 映射格式
        const urls = parseUrlMapping(text);

        if (urls.length > 0) {
          // 检测第一个 URL 的类型来决定渲染方式
          const urlType = detectUrlType(urls[0]);

          if (urlType === 'image') {
            return <ImageCell urls={urls} />;
          }

          if (urlType === 'video') {
            return <VideoCell urls={urls} />;
          }
        }

        // 普通文本
        const displayText = text != null ? String(text) : '-';
        return (
          <Tooltip title={displayText} placement="topLeft">
            <span style={{
              color: isResultColumn ? '#1890ff' : 'inherit',
              fontWeight: isResultColumn ? 500 : 'normal',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayText}
            </span>
          </Tooltip>
        );
      };

      return {
        title: (
          <span style={{
            color: isResultColumn ? '#1890ff' : 'inherit',
            fontWeight: isResultColumn ? 'bold' : 'normal'
          }}>
            {col}
            {isResultColumn && (
              <Tag color="blue" style={{ marginLeft: 8 }}>结果列</Tag>
            )}
          </span>
        ),
        dataIndex: col,
        key: col,
        width: 300,
        ellipsis: {
          showTitle: false,
        },
        render: renderCell,
      };
    });
  }, [data]);

  // 导出CSV功能
  const handleExportCSV = () => {
    try {
      if (!data || !data.columns || !data.rows) {
        message.error('没有可导出的数据');
        return;
      }

      // 构建CSV内容
      const headers = data.columns.join(',');
      const rows = data.rows.map(row => {
        return data.columns.map(col => {
          const value = row[col] || '';
          // 处理包含逗号、换行符和引号的值
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',');
      });

      const csvContent = [headers, ...rows].join('\n');

      // 创建Blob并下载
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `试打结果_${filename || 'result'}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      message.success('CSV文件已导出');
    } catch (err) {
      console.error('导出CSV失败:', err);
      message.error('导出失败，请重试');
    }
  };

  if (!data) {
    return null;
  }

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined style={{ fontSize: 20 }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>试打结果预览</span>
          <Tag color="blue">前{data.rows?.length || 0}行</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        padding: '16px 24px'
      }}
      footer={[
        <Button key="export" icon={<DownloadOutlined />} onClick={handleExportCSV}>
          导出CSV
        </Button>,
        <Button key="close" onClick={onClose}>
          继续调整
        </Button>,
        onConfirm && (
          <Button
            key="confirm"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={onConfirm}
          >
            满意，开始全量打标
          </Button>
        ),
      ]}
    >
      {/* 提示信息 - 移到顶部 */}
      <div style={{
        marginBottom: 16,
        padding: '12px 16px',
        background: '#fffbe6',
        borderRadius: 6,
        border: '1px solid #ffe58f'
      }}>
        <Text style={{ fontSize: 14, color: '#ad6800' }}>
          💡 <Text strong style={{ color: '#ad6800' }}>提示：</Text>如果结果不满意，请关闭此窗口并修改Prompt，然后再次点击"试打前20行"按钮
        </Text>
      </div>

      {/* 文件信息 */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          文件：<Text strong>{filename}</Text> |
          总列数：<Text strong>{data.columns?.length || 0}</Text> |
          数据行数：<Text strong>{data.rows?.length || 0}</Text> |
          结果列：<Tag color="blue">{data.result_column}</Tag>
        </Text>
      </div>

      {/* 结果表格 */}
      <Table
        columns={columns}
        dataSource={data.rows || []}
        rowKey={(record, index) => index}
        pagination={false}
        scroll={{
          x: (data.columns?.length || 0) * 300,
          y: 'calc(100vh - 400px)'
        }}
        size="small"
        bordered
        sticky
      />
    </Modal>
  );
};

export default TrialResultModal;
